import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import type { ChartData, ChartOptions, ScriptableContext } from "chart.js";
import { useChartTheme } from "@/lib/chartTheme";
import { registerCharts } from "./register";
import { mergeOptions, withAlpha } from "./utils";

registerCharts();

export interface BarSeries {
  label: string;
  data: number[];
  color?: string;
  focal?: boolean;
}

interface BarChartProps {
  labels: string[];
  series: BarSeries[];
  /** "vertical" (default) or "horizontal" bars. */
  orientation?: "vertical" | "horizontal";
  stacked?: boolean;
  showLegend?: boolean;
  formatValue?: (value: number) => string;
  height?: number;
  className?: string;
  /** Delay (ms) before the entry animation starts. */
  delay?: number;
}

/** Vertical gradient on each bar — colour at top, fades down. */
function barGradient(colour: string, orientation: "vertical" | "horizontal") {
  return (ctx: ScriptableContext<"bar">) => {
    const { chart } = ctx;
    const { ctx: cv, chartArea } = chart;
    if (!chartArea) return withAlpha(colour, 0.85);
    const g = orientation === "vertical"
      ? cv.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
      : cv.createLinearGradient(chartArea.right, 0, chartArea.left, 0);
    g.addColorStop(0, colour);
    g.addColorStop(1, withAlpha(colour, 0.55));
    return g;
  };
}

export function BarChart({
  labels,
  series,
  orientation = "vertical",
  stacked = false,
  showLegend = false,
  formatValue,
  height = 240,
  className,
  delay = 0,
}: BarChartProps) {
  const { tokens, options: base, palette } = useChartTheme();

  const data = useMemo<ChartData<"bar">>(() => {
    return {
      labels,
      datasets: series.map((s, i) => {
        const colour = s.color ?? (s.focal ? tokens.accent : palette[(i + 1) % palette.length]);
        return {
          label: s.label,
          data: s.data,
          backgroundColor: barGradient(colour, orientation),
          hoverBackgroundColor: colour,
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 28,
        };
      }),
    };
  }, [series, tokens, palette, orientation]);

  const gridColor = withAlpha(tokens.border, 0.4);

  const options = useMemo<ChartOptions<"bar">>(() => {
    return mergeOptions(base as ChartOptions<"bar">, {
      indexAxis: orientation === "horizontal" ? "y" : "x",
      plugins: { legend: { display: showLegend } },
      scales: {
        x: {
          stacked,
          grid: orientation === "horizontal"
            ? { color: gridColor, drawTicks: false, lineWidth: 1 }
            : { display: false },
        },
        y: {
          stacked,
          grid: orientation === "vertical"
            ? { color: gridColor, drawTicks: false, lineWidth: 1 }
            : { display: false },
          ticks: formatValue
            ? { callback: (v) => formatValue(Number(v)) }
            : undefined,
        },
      },
      animation: {
        duration: 800,
        easing: "easeOutQuart",
        delay,
      },
    } as ChartOptions<"bar">);
  }, [base, orientation, stacked, showLegend, formatValue, gridColor, delay]);

  return (
    <div className={className} style={{ height }}>
      <Bar data={data} options={options} />
    </div>
  );
}
